import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input, Select } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { NoteReviewSection } from "./NoteReviewSection";
import { ReportedProblemsSection } from "./ReportedProblemsSection";

// =============================================================================
// API contracts — mirrors of DashboardController responses
// =============================================================================

interface DashboardSummary {
  todayCost: number;
  monthCost: number;
  todayFormsCount: number;
}

interface TrendPoint {
  date: string; // YYYY-MM-DD (business-local day)
  cost: number;
  forms: number;
}

type SessionStatus =
  | "InProgress"
  | "Completed"
  | "Exported"
  | "Cancelled"
  | "Error";

interface SessionsPage {
  total: number;
  page: number;
  pageSize: number;
  items: ProcessingSessionSummary[];
}

interface ProcessingSessionSummary {
  id: string;
  timestamp: string;
  fileType: string;
  fileName: string;
  totalObjections: number;
  resolvedObjections: number;
  processingTimeMs: number;
  totalCost: number;
  status: SessionStatus;
  producedPdf: boolean;
  producedExcel: boolean;
  errorMessage?: string | null;
  userId?: string | null;
}

// Coarse list filter — mirrors the server's SessionStatusFilter enum.
type StatusFilter = "All" | "Completed" | "InProgress" | "Failed" | "Cancelled";

const TREND_DAYS = 30;
const PAGE_SIZE = 25;

export function DashboardPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("Completed");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // 1-based; any filter change jumps back to the first page.
  const [page, setPage] = useState(1);

  const summary = useQuery<DashboardSummary>({
    queryKey: ["dashboard", "summary"],
    queryFn: () => api<DashboardSummary>("/api/dashboard/summary"),
    refetchInterval: 30_000, // 30s auto-refresh — same cadence as Blazor
  });

  const trend = useQuery<TrendPoint[]>({
    queryKey: ["dashboard", "trend"],
    queryFn: () => api<TrendPoint[]>(`/api/dashboard/trend?days=${TREND_DAYS}`),
    refetchInterval: 60_000,
  });

  const sessionsParams = new URLSearchParams({
    status: statusFilter,
    page: String(page),
    pageSize: String(PAGE_SIZE),
  });
  if (fromDate) sessionsParams.set("from", fromDate);
  if (toDate) sessionsParams.set("to", toDate);
  const sessions = useQuery<SessionsPage>({
    queryKey: ["dashboard", "sessions", statusFilter, fromDate, toDate, page],
    queryFn: () => api<SessionsPage>(`/api/dashboard/sessions?${sessionsParams}`),
    refetchInterval: 30_000,
    // Keep the previous page on screen while the next one loads — no flash.
    placeholderData: keepPreviousData,
  });

  // If a filter change strands us past the last page (or rows disappear on
  // refresh), snap back to a page that exists.
  const total = sessions.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (sessions.data && page > lastPage) setPage(lastPage);
  }, [sessions.data, page, lastPage]);

  const resetPageAnd = (apply: () => void) => {
    setPage(1);
    apply();
  };

  // 30-day totals + average cost per form, derived from the trend.
  const trendCost = (trend.data ?? []).reduce((sum, p) => sum + p.cost, 0);
  const trendForms = (trend.data ?? []).reduce((sum, p) => sum + p.forms, 0);
  const avgCostPerForm = trendForms > 0 ? trendCost / trendForms : null;

  return (
    <div className="animate-reveal">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label mb-2">Activity</div>
          <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
            Dashboard
          </h1>
        </div>
        {summary.isFetching && !summary.isLoading && (
          <div className="text-[0.82rem] text-ink-muted">Refreshing…</div>
        )}
      </div>

      {summary.isLoading ? (
        <SkeletonGrid />
      ) : summary.isError ? (
        <ErrorBanner message={errorMessage(summary.error)} />
      ) : summary.data ? (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="Forms today"
              value={summary.data.todayFormsCount.toString()}
              tint="primary"
            />
            <Metric
              label="OpenAI today"
              value={fmtCurrency(summary.data.todayCost)}
              tint="sage"
            />
            <Metric
              label="OpenAI this month"
              value={fmtCurrency(summary.data.monthCost)}
              tint="butter"
            />
            <Metric
              label="Avg cost / form · 30d"
              value={avgCostPerForm != null ? fmtCurrency(avgCostPerForm) : "—"}
              tint="sky"
            />
          </div>

          {trend.data && <TrendCard points={trend.data} cost={trendCost} forms={trendForms} />}

          <section className="mt-10 rounded-lg border border-rule bg-surface shadow-soft">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-6 py-3.5">
              <div className="text-[0.95rem] font-semibold text-ink">Sessions</div>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  aria-label="Status filter"
                  value={statusFilter}
                  onChange={(e) =>
                    resetPageAnd(() => setStatusFilter(e.target.value as StatusFilter))
                  }
                  className="w-auto px-2.5 py-1.5 text-[0.86rem]"
                >
                  <option value="Completed">Completed</option>
                  <option value="InProgress">In progress</option>
                  <option value="Failed">Failed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="All">All</option>
                </Select>
                <Input
                  aria-label="From date"
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => resetPageAnd(() => setFromDate(e.target.value))}
                  className="w-auto px-2.5 py-1.5 text-[0.86rem]"
                />
                <span className="text-[0.82rem] text-ink-muted">to</span>
                <Input
                  aria-label="To date"
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => resetPageAnd(() => setToDate(e.target.value))}
                  className="w-auto px-2.5 py-1.5 text-[0.86rem]"
                />
                {(fromDate || toDate) && (
                  <button
                    type="button"
                    onClick={() =>
                      resetPageAnd(() => {
                        setFromDate("");
                        setToDate("");
                      })
                    }
                    className="text-[0.82rem] font-medium text-primary hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </header>
            {sessions.isLoading ? (
              <div className="px-6 py-10 text-center text-[0.92rem] text-ink-muted">
                Loading sessions…
              </div>
            ) : sessions.isError ? (
              <div className="px-6 py-6">
                <ErrorBanner message={errorMessage(sessions.error)} />
              </div>
            ) : !sessions.data || sessions.data.items.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <div className="label mb-1">No sessions</div>
                <p className="text-[0.92rem] text-ink-muted">
                  {statusFilter !== "All" || fromDate || toDate
                    ? "Nothing matches these filters."
                    : "Process a file from the Upload page to see activity here."}
                </p>
              </div>
            ) : (
              <div className="max-h-[26rem] overflow-y-auto">
              <table className="w-full text-[0.92rem]">
                <thead className="sticky top-0 z-10 bg-canvas-2 text-[0.78rem] font-semibold uppercase tracking-wider text-ink-muted">
                  <tr>
                    <Th className="pl-6">When</Th>
                    <Th>Type</Th>
                    <Th>File</Th>
                    <Th className="text-right">Items</Th>
                    <Th className="text-right">Time</Th>
                    <Th className="text-right">Cost</Th>
                    <Th className="pr-6 text-right">Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {sessions.data.items.map((s) => (
                    <tr key={s.id} className="hover:bg-canvas-2/40">
                      <Td className="pl-6 text-ink-muted">
                        {fmtTime(s.timestamp)}
                      </Td>
                      <Td>
                        <FileTypeChip type={s.fileType} />
                      </Td>
                      <Td className="max-w-[260px] text-ink">
                        <div className="truncate">{s.fileName}</div>
                        {s.userId && (
                          <div className="truncate text-[0.74rem] text-ink-muted">
                            by {userLabel(s.userId)}
                          </div>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums text-ink">
                        <div className="flex flex-col items-end leading-tight">
                          <span>{s.totalObjections}</span>
                          <span className="text-[0.7rem] text-ink-muted">
                            {itemsLabel(s)}
                          </span>
                        </div>
                      </Td>
                      <Td className="text-right tabular-nums text-ink-muted">
                        {fmtMs(s.processingTimeMs)}
                      </Td>
                      <Td className="text-right tabular-nums text-ink">
                        {fmtCurrency(s.totalCost)}
                      </Td>
                      <Td className="pr-6 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <StatusBadge
                            status={s.status}
                            errorMessage={s.errorMessage}
                          />
                          <OutputLabel
                            producedPdf={s.producedPdf}
                            producedExcel={s.producedExcel}
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {sessions.data && sessions.data.total > PAGE_SIZE && (
              <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-rule px-6 py-3">
                <div className="text-[0.82rem] text-ink-muted">
                  {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, sessions.data.total)} of{" "}
                  {sessions.data.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={page <= 1 || sessions.isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    disabled={page >= lastPage || sessions.isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </footer>
            )}
          </section>

          {/* How the PAA change notes are landing. Last because it is the
              slowest-moving number here — and the most important one, since the
              written spec for how these should read was walked back and this is
              the only evidence that replaced it. */}
          <NoteReviewSection />
          {/* Directly under the note log: the same learning loop, and the half
              that log cannot express — why a note was changed, and what we
              never mentioned at all. */}
          <ReportedProblemsSection />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Metric({
  label,
  value,
  tint,
}: {
  label: string;
  value: string;
  tint: "primary" | "sage" | "butter" | "sky";
}) {
  const map = {
    primary: "bg-primary-soft text-primary-ink",
    sage: "bg-sage-soft text-sage-ink",
    butter: "bg-butter-soft text-butter-ink",
    sky: "bg-sky-soft text-sky-ink",
  }[tint];

  return (
    <Card className="p-6">
      <div
        className={`inline-flex rounded-md px-2 py-0.5 text-[0.78rem] font-medium ${map}`}
      >
        {label}
      </div>
      <div className="mt-4 text-[2.75rem] font-semibold leading-none tabular-nums text-ink">
        {value}
      </div>
    </Card>
  );
}

// 30-day spend, one bar per business-local day. Hover a bar for the exact
// spend + forms count. Totals live in the header so the chart stays quiet.
function TrendCard({
  points,
  cost,
  forms,
}: {
  points: TrendPoint[];
  cost: number;
  forms: number;
}) {
  const maxCost = Math.max(...points.map((p) => p.cost), 0.000001);

  return (
    <section className="mt-10 rounded-lg border border-rule bg-surface p-6 shadow-soft">
      <header className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
        <div className="text-[0.95rem] font-semibold text-ink">
          Last {points.length} days
        </div>
        <div className="text-[0.82rem] text-ink-muted">
          {fmtCurrency(cost)} spent · {forms} {forms === 1 ? "form" : "forms"}
        </div>
      </header>
      <div className="flex h-24 items-end gap-[3px]" role="img" aria-label="Daily OpenAI spend, last 30 days">
        {points.map((p) => (
          <div
            key={p.date}
            title={`${fmtDay(p.date)} — ${fmtCurrency(p.cost)} · ${p.forms} ${p.forms === 1 ? "form" : "forms"}`}
            className="group flex h-full flex-1 items-end"
          >
            <div
              className={cn(
                "w-full rounded-t-sm transition-colors",
                p.cost > 0
                  ? "bg-primary-soft group-hover:bg-primary"
                  : "bg-canvas-2",
              )}
              style={{
                height: p.cost > 0 ? `${Math.max((p.cost / maxCost) * 100, 6)}%` : "3px",
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[0.74rem] text-ink-muted">
        <span>{fmtDay(points[0]?.date)}</span>
        <span>{fmtDay(points[points.length - 1]?.date)}</span>
      </div>
    </section>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="p-6">
          <div className="h-5 w-24 animate-pulse rounded-md bg-canvas-2" />
          <div className="mt-4 h-10 w-32 animate-pulse rounded-md bg-canvas-2" />
        </Card>
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3.5 text-[0.92rem] text-ink">
      <span className="font-medium text-danger">Failed to load dashboard:</span>{" "}
      <span className="text-ink-soft">{message}</span>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={cn("px-3 py-2.5 text-left", className)}>{children}</th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn("px-3 py-2.5", className)}>{children}</td>;
}

// Status badge — replaces the old OK/Failed boolean. "InProgress" renders as
// "Incomplete" (started but no outcome reported — running or abandoned), so an
// abandoned session is no longer indistinguishable from a genuine failure.
function StatusBadge({
  status,
  errorMessage,
}: {
  status: SessionStatus;
  errorMessage?: string | null;
}) {
  const config: Record<SessionStatus, { label: string; cls: string }> = {
    Completed: { label: "Completed", cls: "bg-sage-soft text-sage-ink" },
    Exported: { label: "Exported", cls: "bg-sky-soft text-sky-ink" },
    InProgress: { label: "Incomplete", cls: "bg-canvas-2 text-ink-muted" },
    Cancelled: { label: "Cancelled", cls: "bg-lilac-soft text-lilac-ink" },
    Error: { label: "Failed", cls: "bg-danger-soft text-danger" },
  };
  const { label, cls } = config[status] ?? config.InProgress;
  return (
    <span
      title={status === "Error" ? errorMessage ?? undefined : undefined}
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[0.74rem] font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

// Which deliverables came out of the session, shown beneath the status badge.
function OutputLabel({
  producedPdf,
  producedExcel,
}: {
  producedPdf: boolean;
  producedExcel: boolean;
}) {
  const parts = [
    producedPdf ? "PDF" : null,
    producedExcel ? "Excel" : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <span className="text-[0.7rem] text-ink-muted">{parts.join(" + ")}</span>
  );
}

function FileTypeChip({ type }: { type: string }) {
  const palette: Record<string, string> = {
    PDF: "bg-primary-soft text-primary-ink",
    Excel: "bg-butter-soft text-butter-ink",
    CSV: "bg-sage-soft text-sage-ink",
    BAX: "bg-lilac-soft text-lilac-ink",
    Batch: "bg-sky-soft text-sky-ink",
    PAA: "bg-primary-soft text-primary-ink",
  };
  const cls = palette[type] ?? "bg-canvas-2 text-ink-muted";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[0.74rem] font-medium ${cls}`}
    >
      {type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

// "Items" means sheets for a PAA run and objections otherwise — the unit label
// keeps the shared column honest across the two flows.
function itemsLabel(s: ProcessingSessionSummary): string {
  if (s.fileType === "PAA") return "sheets";
  return s.resolvedObjections > 0
    ? `obj. · ${s.resolvedObjections} resolved`
    : "objections";
}

// "user@domain.com" → "user" — enough to tell teammates apart in the table.
function userLabel(email: string): string {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function fmtCurrency(n: number): string {
  // Stats costs can be sub-cent (e.g. $0.0023). Show 4 sig figs for < $1,
  // 2dp otherwise.
  if (n === 0) return "$0.00";
  if (n < 1) return "$" + n.toFixed(4);
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.floor((ms % 60_000) / 1000);
  return `${min}m ${sec}s`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// "2026-06-05" → "Jun 5". Parsed manually — new Date("YYYY-MM-DD") would
// interpret the string as UTC midnight and shift the day in NY time.
function fmtDay(date: string | undefined): string {
  if (!date) return "";
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
